import { DetailedHTMLProps, FC, ForwardedRef, HTMLAttributes, forwardRef, useMemo } from 'react';
import Avatar from './Avatar';
import { AvatarOptions } from '../helpers/avatar.types';
import { getFontSize } from '../helpers/avatar.utils';
import { twMerge } from 'tailwind-merge';

interface AvatarGroupProps
    extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
        Pick<AvatarOptions, 'size' | 'fontSize' | 'initialsOnly'> {
    avatars: Pick<AvatarOptions, 'src' | 'alt' | 'backgroundColor'>[];
    max?: number;
    ref?: ForwardedRef<HTMLDivElement>;
}

const AvatarGroup = forwardRef<HTMLDivElement, AvatarGroupProps>((props, ref) => {
    const { avatars, max = 4, size, fontSize, initialsOnly, className, ...rest } = props;

    const altFontSize = useMemo(() => (fontSize ? fontSize : getFontSize(size)), [size, fontSize]);

    return (
        <div {...rest} ref={ref} className={twMerge('flex items-center', className)}>
            {avatars.slice(0, max).map((row, i) => (
                <Avatar
                    key={i}
                    src={row.src}
                    alt={row.alt}
                    backgroundColor={row.backgroundColor}
                    size={size}
                    initialsOnly={initialsOnly}
                    className="border-2 border-card-color -ml-2 first:ml-0"
                />
            ))}

            {avatars.length > max && (
                <Avatar
                    alt={`+${avatars.length - max}`}
                    size={size}
                    backgroundColor="rgb(var(--background-color))"
                    className="text-grayscale-400 tracking-tighter font-semibold border-2 border-card-color -ml-2"
                    initialsOnly={false}
                    fontSize={altFontSize - altFontSize / 6}
                />
            )}
        </div>
    );
});

export default AvatarGroup;
